import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosApi from 'src/app/config';

// Types
export interface Client {
  organizationId: string;
  organizationName: string;
  companyType: 'CUSTOMER' | 'SUPPLIER' | 'STONE_MINES' | 'STONE_CRUSHER' | 'COAL_MINES' | 'CONTRACTOR' | 'CONSTRUCTION_COMPANY' | 'OTHER';
  city?: string;
  state?: string;
  phone?: string;
  email?: string;
  contactPerson?: string;
  businessSince?: string;
  status?: 'Active' | 'Inactive';
  partnershipStatus?: 'Outstanding' | 'Excellent' | 'Good' | 'Fair' | 'Poor';
  totalBusiness?: number;
  monthlyBusiness?: number;
  lastTransaction?: string;
  paymentDays?: number;
  pendingBalance?: number;
  vehicles?: any[];
  transactions?: any[];
  // Basic endpoint fields
  aliasName?: string;
  relationshipType?: string;
  linkId?: string;
  lastInteractionAt?: string;
}

interface ClientsState {
  clients: Client[];
  selectedClient: Client | null;
  selectedClientId: string | null;
  searchTerm: string;
  filterType: 'all' | 'customers' | 'suppliers';
  isLoading: boolean;
  isLoadingClientDetails: boolean; // Track client details loading separately
  error: string | null;
  lastFetchedOrganizationId: string | null; // Track last fetched org to prevent duplicates
}

// Async thunks
export const createClient = createAsyncThunk(
  'clients/createClient',
  async (thunkAPI: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, clientData } = thunkAPI;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).post('/clients', clientData);
      
      return fulfillWithValue(response.data);
    } catch (error: any) {
      console.error('Error creating client:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const fetchClients = createAsyncThunk(
  'clients/fetchClients',
  async (thunkAPI: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId } = thunkAPI;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).get(`/clients`);
      
      // Backend now returns clients directly in the correct format
      const clients: Client[] = response.data.data.clients || [];
      
      return fulfillWithValue(clients);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchClientsBasic = createAsyncThunk(
  'clients/fetchClientsBasic',
  async (thunkAPI: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, query, relationshipType, limit } = thunkAPI;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      const params = new URLSearchParams();
      if (query) params.append('query', query);
      if (relationshipType) params.append('relationshipType', relationshipType);
      if (limit) params.append('limit', limit.toString());

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).get(`/clients/basic?${params.toString()}`);
      
      // Backend returns basic client data
      const clients = response.data.data.clients || [];
      
      return fulfillWithValue(clients);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchClientDetails = createAsyncThunk(
  'clients/fetchClientDetails',
  async (thunkAPI: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, clientId } = thunkAPI;
      
      if (!organizationId || !clientId) {
        return rejectWithValue("Organization ID and Client ID are required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).get(`/clients/${clientId}`);
      
      // Transform backend response to match frontend Client interface
      const clientData = response.data.data;
      const client: Client = {
        organizationId: clientData.organizationId,
        organizationName: clientData.organizationName,
        companyType: clientData.companyType || 'CUSTOMER',
        city: clientData.city,
        state: clientData.state,
        phone: clientData.phone,
        email: clientData.email,
        contactPerson: clientData.contactPerson,
        businessSince: clientData.businessSince,
        status: clientData.status,
        partnershipStatus: clientData.partnershipStatus || 'Good',
        totalBusiness: clientData.totalBusiness || 0,
        monthlyBusiness: clientData.monthlyBusiness || 0,
        lastTransaction: clientData.lastTransaction || '',
        paymentDays: clientData.paymentDays || 0,
        vehicles: clientData.vehicles || [],
        transactions: clientData.transactions || []
      };
      
      return fulfillWithValue(client);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial state
const initialState: ClientsState = {
  clients: [],
  selectedClient: null,
  selectedClientId: null,
  searchTerm: '',
  filterType: 'all',
  isLoading: false,
  isLoadingClientDetails: false,
  error: null,
  lastFetchedOrganizationId: null,
};

// Slice
const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    setSelectedClientId: (state, action) => {
      state.selectedClientId = action.payload;
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setFilterType: (state, action) => {
      state.filterType = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedClient: (state) => {
      state.selectedClient = null;
    },
    resetState: (state) => {
      state.clients = [];
      state.selectedClient = null;
      state.selectedClientId = null;
      state.error = null;
      state.lastFetchedOrganizationId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch clients
      .addCase(fetchClients.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clients = action.payload;
        state.error = null;
        // Note: We can't easily get organizationId here from action payload
        // Will track it in the hook instead
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (typeof action.payload === 'string' ? action.payload : null) || 
                     action.error?.message || 
                     'Failed to fetch clients';
        // Keep existing clients on error to prevent clearing the list
      })
      // Fetch clients basic (lightweight)
      .addCase(fetchClientsBasic.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchClientsBasic.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clients = action.payload;
        state.error = null;
      })
      .addCase(fetchClientsBasic.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (typeof action.payload === 'string' ? action.payload : null) || 
                     action.error?.message || 
                     'Failed to fetch clients';
      })
      // Fetch client details
      .addCase(fetchClientDetails.pending, (state) => {
        state.isLoadingClientDetails = true;
      })
      .addCase(fetchClientDetails.fulfilled, (state, action) => {
        state.isLoadingClientDetails = false;
        state.selectedClient = action.payload;
      })
      .addCase(fetchClientDetails.rejected, (state, action) => {
        state.isLoadingClientDetails = false;
        state.error = (typeof action.payload === 'string' ? action.payload : null) ||
                     action.error?.message ||
                     'Failed to fetch client details';
      })
      // Create client
      .addCase(createClient.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createClient.fulfilled, (state, action) => {
        state.isLoading = false;
        // Optionally add the new client to the list or refresh
      })
      .addCase(createClient.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (typeof action.payload === 'string' ? action.payload : null) || 
                     action.error?.message || 
                     'Failed to create client';
      });
  },
});

export const { 
  setSelectedClientId, 
  setSearchTerm, 
  setFilterType, 
  clearError, 
  clearSelectedClient,
  resetState 
} = clientsSlice.actions;

export default clientsSlice.reducer;